/**
 * Servitix service-area ZIP guard.
 *
 * Validates ZIP-code inputs on lead/contact forms against a Metro Atlanta
 * allowlist before submission. If the ZIP is missing, malformed, or outside
 * the service area, the submission is blocked and an inline error is shown.
 *
 * To enable on a form: add `data-svx-zip-guard` to the <form> tag, and
 * include a ZIP input whose name or id contains "zip" or "postal".
 */
(function () {
  'use strict';

  // Metro Atlanta service-area ZIPs. Source of truth: 'Servitix County and City
  // wise Zipcodes.xlsx' (Allowed rows only). Excluded ZIPs are rejected.
  var SERVITIX_SERVICE_ZIPS = new Set([
    // Clayton: Ellenwood, Forest Park, Jonesboro, Morrow, Rex, Riverdale
    '30236', '30237', '30238', '30260', '30273', '30274', '30287', '30294',
    '30296', '30297', '30298',
    // Cobb: Kennesaw, Marietta, Smyrna
    '30006', '30007', '30008', '30060', '30061', '30062', '30063', '30064',
    '30065', '30066', '30067', '30068', '30069', '30080', '30081', '30082',
    '30090', '30144', '30152', '30156', '30160', '31144',
    // DeKalb: Atlanta, Avondale Estates, Clarkston, Decatur, Lithonia, Pine Lake, Scottdale, Stone Mountain, Tucker
    '30002', '30021', '30030', '30031', '30032', '30033', '30034', '30035',
    '30036', '30037', '30038', '30058', '30072', '30079', '30083', '30084',
    '30085', '30086', '30087', '30088', '30319', '30322', '30329', '30333',
    '30340', '30341', '30345', '30346', '30350', '30356', '30359', '30360',
    '30362', '30366', '31119', '31141', '31145', '31146', '39901',
    // Fayette: Fayetteville, Peachtree City
    '30214', '30215', '30269', '31169',
    // Forsyth: Cumming
    '30028', '30040', '30041',
    // Fulton: Alpharetta, Atlanta, Fairburn, Palmetto, Red Oak, Roswell, Union City
    '30004', '30005', '30009', '30022', '30023', '30075', '30076', '30077',
    '30213', '30268', '30272', '30291', '30301', '30302', '30303', '30304',
    '30305', '30306', '30307', '30308', '30309', '30310', '30311', '30312',
    '30313', '30314', '30315', '30316', '30317', '30318', '30320', '30321',
    '30324', '30325', '30326', '30327', '30328', '30331', '30332', '30334',
    '30336', '30337', '30338', '30339', '30342', '30343', '30344', '30348',
    '30349', '30353', '30354', '30355', '30357', '30358', '30361', '30363',
    '30364', '30368', '30369', '30370', '30371', '30374', '30375', '30377',
    '30378', '30380', '30384', '30385', '30388', '30392', '30394', '30396',
    '30398', '31106', '31107', '31126', '31131', '31136', '31139', '31150',
    '31156', '31192', '31193', '31195', '31196',
    // Gwinnett: Buford, Dacula, Duluth, Grayson, Lawrenceville, Lilburn, Loganville, Norcross, Peachtree Corners, Snellville, Suwanee
    '30003', '30010', '30017', '30019', '30024', '30039', '30042', '30043',
    '30044', '30045', '30046', '30047', '30048', '30049', '30052', '30071',
    '30078', '30091', '30092', '30093', '30095', '30096', '30097', '30098',
    '30099', '30515', '30518', '30519',
    // Hall: Braselton, Flowery Branch, Oakwood
    '30517', '30542', '30566',
    // Henry: Mcdonough, Stockbridge
    '30252', '30253', '30281',
    // Rockdale: Conyers
    '30012', '30013', '30094'
  ]);

  var ERROR_CLASS = 'svx-zip-error';
  var ERROR_STYLE = 'color:#b8241f;font-size:0.9rem;margin-top:6px;font-weight:600;';
  var SUPPORT_PHONE = '(770) 469-4294';

  function findZipInput(form) {
    return form.querySelector(
      'input[name*="zip" i], input[name*="postal" i], input[id*="zip" i], input[id*="postal" i]'
    );
  }

  function showError(form, zipInput, message) {
    var err = form.querySelector('.' + ERROR_CLASS);
    if (!err) {
      err = document.createElement('p');
      err.className = ERROR_CLASS;
      err.setAttribute('role', 'alert');
      err.style.cssText = ERROR_STYLE;
      var parent = zipInput.parentNode;
      if (parent) {
        parent.insertBefore(err, zipInput.nextSibling);
      } else {
        form.appendChild(err);
      }
    }
    err.textContent = message;
  }

  function clearError(form) {
    var err = form.querySelector('.' + ERROR_CLASS);
    if (err && err.parentNode) {
      err.parentNode.removeChild(err);
    }
  }

  function handleSubmit(event) {
    var form = event.currentTarget;
    var zipInput = findZipInput(form);
    if (!zipInput) {
      // No ZIP field on this form — nothing to guard.
      return;
    }
    var raw = zipInput.value == null ? '' : String(zipInput.value).trim();
    var zip = raw.replace(/\D/g, '').slice(0, 5);

    if (!/^\d{5}$/.test(zip) || !SERVITIX_SERVICE_ZIPS.has(zip)) {
      event.preventDefault();
      event.stopPropagation();
      var label = raw === '' ? '(empty)' : raw;
      var message =
        "Sorry, we don't currently service ZIP code " + label +
        '. Call ' + SUPPORT_PHONE + ' for special-area requests.';
      showError(form, zipInput, message);
      try {
        zipInput.focus({ preventScroll: false });
      } catch (e) {
        zipInput.focus();
      }
      return false;
    }

    clearError(form);
  }

  // Forms are matched either by the `data-svx-zip-guard` attribute on the
  // <form> tag itself, or by a parent wrapper with the same attribute. The
  // wrapper fallback exists because Shopify's `{% form 'contact' %}` helper
  // does not accept hyphenated parameter keys, so we tag the surrounding
  // container in those sections instead.
  function getGuardedForms() {
    var seen = new Set();
    var result = [];

    var direct = document.querySelectorAll('form[data-svx-zip-guard]');
    for (var i = 0; i < direct.length; i++) {
      if (!seen.has(direct[i])) {
        seen.add(direct[i]);
        result.push(direct[i]);
      }
    }

    var wrappers = document.querySelectorAll('[data-svx-zip-guard]');
    for (var j = 0; j < wrappers.length; j++) {
      var wrapper = wrappers[j];
      if (wrapper.tagName === 'FORM') continue;
      var nested = wrapper.querySelectorAll('form');
      for (var k = 0; k < nested.length; k++) {
        if (!seen.has(nested[k])) {
          seen.add(nested[k]);
          result.push(nested[k]);
        }
      }
    }

    return result;
  }

  function bindForms() {
    var forms = getGuardedForms();
    for (var i = 0; i < forms.length; i++) {
      var form = forms[i];
      if (form.dataset.svxZipGuardBound === '1') continue;
      form.dataset.svxZipGuardBound = '1';
      // Use capture phase so we run before other submit handlers (e.g. the
      // lead-capture button-busy handler) and can fully cancel submission.
      form.addEventListener('submit', handleSubmit, true);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bindForms);
  } else {
    bindForms();
  }

  // Re-bind when Shopify section editor reloads a section.
  document.addEventListener('shopify:section:load', bindForms);
})();
